// Game Audio — Web Audio API sound effects for Focus Catcher
// Pattern follows AudioGuide.jsx: lazy AudioContext init on first user gesture.
// All sounds are generated programmatically (no audio files).

import { getAudioEnabled } from './localStorage';

class GameAudio {
    constructor() {
        this.ctx = null;
        this.enabled = getAudioEnabled();
        this.isUnlocked = false;
    }

    setEnabled(enabled) {
        this.enabled = enabled;
    }

    unlock() {
        if (this.isUnlocked && this.ctx) return;
        try {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            if (this.ctx.state === 'suspended') {
                this.ctx.resume();
            }
            this.isUnlocked = true;
        } catch (e) {
            console.warn('GameAudio: could not create AudioContext', e);
        }
    }

    _play(freq, duration, type, volume, pan) {
        if (!this.enabled || !this.ctx) return;
        try {
            const now = this.ctx.currentTime;
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();

            osc.type = type || 'sine';
            osc.frequency.setValueAtTime(freq, now);

            gain.gain.setValueAtTime(volume || 0.15, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

            if (pan !== undefined && this.ctx.createStereoPanner) {
                const panner = this.ctx.createStereoPanner();
                panner.pan.setValueAtTime(pan, now);
                osc.connect(gain).connect(panner).connect(this.ctx.destination);
            } else {
                osc.connect(gain).connect(this.ctx.destination);
            }

            osc.start(now);
            osc.stop(now + duration);
        } catch (e) {
            // Silently fail — audio errors should never break gameplay
        }
    }

    // Crisp pop on target hit. Pitch rises with combo.
    targetHit(combo) {
        const freq = 800 + (combo || 0) * 15;
        this._play(freq, 0.05, 'sine', 0.2);
    }

    // Bright chime on PERFECT speed hit
    perfectHit() {
        this._play(1200, 0.08, 'sine', 0.15);
        setTimeout(() => this._play(1500, 0.06, 'sine', 0.12), 30);
    }

    // Soft low whomp on miss (not harsh)
    targetMiss() {
        this._play(200, 0.15, 'sine', 0.1);
    }

    // Short buzz on decoy tapped
    decoyTapped() {
        this._play(150, 0.06, 'sawtooth', 0.12);
    }

    // Golden target sparkle arpeggio
    goldenHit() {
        this._play(523, 0.03, 'sine', 0.15); // C5
        setTimeout(() => this._play(659, 0.03, 'sine', 0.15), 30); // E5
        setTimeout(() => this._play(784, 0.03, 'sine', 0.15), 60); // G5
    }

    // Combo milestone — ascending chime
    comboMilestone(tier) {
        const baseFreq = 500 + tier * 100;
        this._play(baseFreq, 0.08, 'sine', 0.15);
        setTimeout(() => this._play(baseFreq * 1.25, 0.08, 'sine', 0.12), 80);
    }

    // Level complete — triumphant chord
    levelComplete() {
        this._play(523, 0.3, 'sine', 0.12); // C
        setTimeout(() => this._play(659, 0.25, 'sine', 0.10), 60);  // E
        setTimeout(() => this._play(784, 0.2, 'sine', 0.10), 120);  // G
        setTimeout(() => this._play(1047, 0.3, 'sine', 0.08), 200); // C octave
    }

    // Star earned ding
    starEarned(index) {
        const freq = 800 + index * 200;
        setTimeout(() => this._play(freq, 0.1, 'sine', 0.15), index * 200);
    }

    // New record fanfare
    newRecord() {
        const notes = [523, 659, 784, 1047];
        notes.forEach((f, i) => {
            setTimeout(() => this._play(f, 0.12, 'sine', 0.12), i * 100);
        });
    }
}

const gameAudio = new GameAudio();
export default gameAudio;
